import React, { useState, useEffect } from 'react';
import { api } from '../utils/api';
import './AuditLogsScreen.css';

const EVENT_META = {
  'session.created':          { label: 'Session created',       color: 'purple' },
  'session.verified':         { label: 'Session verified',      color: 'green'  },
  'session.failed':           { label: 'Session failed',        color: 'red'    },
  'session.expired':          { label: 'Session expired',       color: 'red'    },
  'token.exchanged':          { label: 'Token exchanged',       color: 'purple' },
  'token.rejected':           { label: 'Token rejected',        color: 'orange' },
  'api_key.generated':        { label: 'API key generated',     color: 'grey'   },
  'wallet.enabled':           { label: 'Wallet enabled',        color: 'grey'   },
  'wallet.disabled':          { label: 'Wallet disabled',       color: 'grey'   },
  'branding.updated':         { label: 'Branding updated',      color: 'grey'   },
  'integration.connected':    { label: 'Integration connected', color: 'green'  },
  'integration.disconnected': { label: 'Integration removed',   color: 'orange' },
  'google_wallet.certificate.issued':  { label: 'Google Wallet cert issued',  color: 'green'  },
  'google_wallet.certificate.revoked': { label: 'Google Wallet cert revoked', color: 'orange' },
};

function EventBadge({ event }) {
  const meta = EVENT_META[event] || { label: event, color: 'grey' };
  return <span className={`vt-audit-badge vt-audit-badge--${meta.color}`}>{meta.label}</span>;
}

function ActorBadge({ actor }) {
  return <span className={`vt-audit-actor vt-audit-actor--${actor}`}>{actor}</span>;
}

function DetailPanel({ event, onClose }) {
  return (
    <div className="vt-audit-detail">
      <div className="vt-audit-detail-header">
        <div>
          <EventBadge event={event.event} />
          <p className="vt-audit-detail-time">{formatTime(event.created_at)}</p>
        </div>
        <button className="vt-audit-detail-close" onClick={onClose} aria-label="Close">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path d="M2 2L14 14M14 2L2 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>
      <div className="vt-audit-detail-body">
        <Row label="Event ID"   value={event.id}          mono />
        <Row label="Actor"      value={<ActorBadge actor={event.actor} />} />
        {event.session_id && <Row label="Session ID" value={event.session_id} mono />}
        {event.ip_address && <Row label="IP Address" value={event.ip_address} mono />}
        {event.metadata && Object.keys(event.metadata).length > 0 && (
          <div className="vt-audit-detail-metadata">
            <span className="vt-audit-detail-label">Metadata</span>
            <pre className="vt-audit-detail-json">
              {JSON.stringify(event.metadata, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="vt-audit-detail-row">
      <span className="vt-audit-detail-label">{label}</span>
      <span className={`vt-audit-detail-value${mono ? ' vt-mono' : ''}`}>{value}</span>
    </div>
  );
}

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

export default function AuditLogsScreen() {
  const [events, setEvents]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [selected, setSelected]   = useState(null);

  const load = async (p = 0) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getAuditLogs(p);
      setEvents(data.events || []);
      setTotal(data.total  || 0);
      setPage(data.page    || 0);
      setTotalPages(data.total_pages || 0);
    } catch {
      setError('Failed to load audit logs.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(0); }, []);

  return (
    <div className="vt-audit">
      <div className="vt-audit-head">
        <div>
          <div className="vt-eyebrow">Activity</div>
          <h1 className="vt-pageTitle vt-audit-title">Audit logs</h1>
          <p className="vt-audit-sub">
            A record of all actions on your account.
            {total > 0 && <span className="vt-audit-count">{total.toLocaleString()} events</span>}
          </p>
        </div>
      </div>

      <div className="vt-audit-body">
        <div className="vt-audit-table-wrap">
          {error && <p className="vt-audit-error">{error}</p>}

          {loading ? (
            <div className="vt-audit-loading"><div className="vt-spinner" /></div>
          ) : events.length === 0 ? (
            <div className="vt-audit-empty">
              <p>No events yet. Events will appear here as you use Veritage.</p>
            </div>
          ) : (
            <table className="vt-audit-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Event</th>
                  <th>Actor</th>
                  <th>Session ID</th>
                </tr>
              </thead>
              <tbody>
                {events.map(ev => (
                  <tr
                    key={ev.id}
                    className={`vt-audit-row${selected?.id === ev.id ? ' vt-audit-row--active' : ''}`}
                    onClick={() => setSelected(selected?.id === ev.id ? null : ev)}
                  >
                    <td className="vt-audit-td-time vt-mono">{formatTime(ev.created_at)}</td>
                    <td><EventBadge event={ev.event} /></td>
                    <td><ActorBadge actor={ev.actor} /></td>
                    <td className="vt-mono vt-muted">{ev.session_id ? ev.session_id.slice(0, 8) + '…' : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {!loading && totalPages > 1 && (
            <div className="vt-audit-pagination">
              <button className="vt-btn vt-btn--ghostPill vt-btn--sm" disabled={page === 0} onClick={() => load(page - 1)}>
                ← Prev
              </button>
              <span className="vt-audit-page-info">Page {page + 1} of {totalPages}</span>
              <button className="vt-btn vt-btn--ghostPill vt-btn--sm" disabled={page >= totalPages - 1} onClick={() => load(page + 1)}>
                Next →
              </button>
            </div>
          )}
        </div>

        {selected && <DetailPanel event={selected} onClose={() => setSelected(null)} />}
      </div>
    </div>
  );
}
